// =============================================================================
// PaymentsScreen — admin transactions feed (purchases + refunds)
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listPayments,
  stripePaymentIntentUrl,
  type PaymentFeedRow,
  type PaymentMethod,
} from '../lib/payments';
import { formatError } from '../lib/errors';
import { formatVenueDateTime } from '../lib/time';
import { useLocationFilter } from '../lib/location-context';

const PAGE_SIZE = 100;
const REFRESH_MS = 30_000;
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000];

const METHOD_LABEL: Record<PaymentMethod, string> = {
  stripe:        'Stripe',
  cash:          'Contant',
  card_terminal: 'Pin',
  gift_card:     'Cadeaubon',
};

const STATUS_LABEL: Record<string, string> = {
  succeeded: 'Voltooid',
  refunded:  'Terugbetaald',
  failed:    'Mislukt',
  pending:   'In behandeling',
  canceled:  'Geannuleerd',
};

export function PaymentsScreen() {
  const [rows, setRows] = useState<PaymentFeedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [status, setStatus] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const { locationId } = useLocationFilter();

  // Background-refresh retry — same shape as CalendarScreen. First-load
  // failures surface a full error card; once we have data, transient blips
  // keep the stale rows visible and queue a fast retry.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const result = await listPayments({
        fromDate: fromDate || null,
        toDate: toDate || null,
        paymentMethod: paymentMethod || null,
        status: status || null,
        search: search || null,
        locationId,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(result.rows);
      setTotal(result.total);
      setLastRefresh(new Date());
      setError(null);
      setRefreshError(null);
      retryAttemptRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    } catch (e) {
      const msg = formatError(e);
      setRows((prev) => {
        if (prev.length === 0) setError(msg);
        else                   setRefreshError(msg);
        return prev;
      });
      if (retryAttemptRef.current < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[retryAttemptRef.current]!;
        retryAttemptRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          void load();
        }, delay);
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, paymentMethod, status, search, locationId, page]);

  useEffect(() => {
    setLoading(true);
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      clearInterval(t);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      retryAttemptRef.current = 0;
    };
  }, [load]);

  // Reset to page 0 whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, paymentMethod, status, search, locationId]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const summary = useMemo(() => {
    let succeeded = 0;
    let refunded = 0;
    for (const r of rows) {
      if (r.status === 'refunded') refunded += r.amount_cents;
      else if (r.status === 'succeeded') succeeded += r.amount_cents;
    }
    return { succeeded, refunded };
  }, [rows]);

  return (
    <div className="screen payments-screen">
      <h1 className="payments-title">Transacties</h1>

      <div className="payments-filters">
        <label className="walkin-field payments-filter">
          <span>Vanaf</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="walkin-field payments-filter">
          <span>T/m</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label className="walkin-field payments-filter">
          <span>Methode</span>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod | '')}
          >
            <option value="">Alle</option>
            <option value="stripe">Stripe</option>
            <option value="cash">Contant</option>
            <option value="card_terminal">Pinterminal</option>
            <option value="gift_card">Cadeaubon (opwaarderen)</option>
          </select>
        </label>
        <label className="walkin-field payments-filter">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Alle</option>
            <option value="succeeded">Voltooid</option>
            <option value="refunded">Terugbetaald</option>
            <option value="failed">Mislukt</option>
            <option value="pending">In behandeling</option>
          </select>
        </label>
        <form
          className="walkin-field payments-filter payments-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <span>Zoek (naam / email / EHV-…)</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Druk op Enter"
          />
        </form>
      </div>

      <div className="payments-summary muted">
        {total} transacties · €{(summary.succeeded / 100).toFixed(2)} voltooid
        {summary.refunded > 0 ? (
          <> · €{(summary.refunded / 100).toFixed(2)} terugbetaald</>
        ) : null}
        {lastRefresh ? (
          <> · ververst {lastRefresh.toLocaleTimeString('nl-NL', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}</>
        ) : null}
        {refreshError ? (
          <> · <span className="cal-meta-refresh-error" title={refreshError}>ververs mislukt</span></>
        ) : null}
        {' '}
        <button className="btn-link" onClick={() => void load()}>Ververs</button>
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && rows.length === 0 ? (
        <div className="muted vouchers-empty">Geen transacties.</div>
      ) : null}

      {rows.length > 0 ? (
        <table className="payments-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Boeking</th>
              <th>Klant</th>
              <th>Beleving</th>
              <th>Methode</th>
              <th className="payments-cell-amount">Bedrag</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <PaymentRow key={r.payment_id} row={r} />
            ))}
          </tbody>
        </table>
      ) : null}

      {totalPages > 1 ? (
        <div className="payments-pagination">
          <button
            className="btn-link"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹ Vorige
          </button>
          <span className="muted">
            Pagina {page + 1} / {totalPages}
          </span>
          <button
            className="btn-link"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Volgende ›
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PaymentRow({ row }: { row: PaymentFeedRow }) {
  // An imported CCV terminal tap: pin at the counter, usually without a
  // booking (see supabase/data-import/ccv).
  const isCcv = row.payment_method === 'card_terminal' && row.payment_method_type === 'ccv';
  const gc = row.kind === 'gift_card_load' ? row.gift_card ?? null : null;
  const fullName =
    (gc ? `Balie${gc.operator_email ? ` (${gc.operator_email})` : ''}` : '') ||
    row.guest_name?.trim() ||
    [row.first_name, row.last_name].filter(Boolean).join(' ') ||
    (isCcv && !row.booking_number ? 'Balie (CCV)' : '—');
  const eur = (row.amount_cents / 100).toFixed(2);
  // A WisePOS (Stripe Terminal) charge is a real Stripe PI whose charge type
  // is card_present — show it as the pin payment it is.
  const isTerminal = row.payment_method === 'stripe' && row.payment_method_type === 'card_present';
  const methodLabel = gc
    ? 'Pin (Stripe)'
    : isTerminal
    ? 'Pin (Stripe)'
    : row.payment_method
      ? METHOD_LABEL[row.payment_method]
      : '—';
  const methodSub = gc
    ? 'Cadeaubon'
    : isCcv
    ? 'CCV'
    : !isTerminal && row.payment_method === 'stripe' && row.payment_method_type
      ? row.payment_method_type
      : null;

  const statusLabel = STATUS_LABEL[row.status] ?? row.status;
  const statusClass =
    row.status === 'succeeded'
      ? 'voucher-status-ok'
      : row.status === 'refunded'
        ? 'voucher-status-warn'
        : 'voucher-status-off';

  return (
    <tr>
      <td className="muted">{formatVenueDateTime(row.created_at)}</td>
      <td>
        {gc ? (
          <Link to={`/cadeaubon/${gc.code}`} className="voucher-code">
            {gc.code_display}
          </Link>
        ) : row.booking_number ? (
          <Link to={`/booking/${row.booking_number}`} className="voucher-code">
            {row.booking_number}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        {fullName}
        {row.customer_email ? (
          <div className="muted voucher-notes">{row.customer_email}</div>
        ) : null}
      </td>
      <td className="muted">{row.experience_name ?? '—'}</td>
      <td>
        {methodLabel}
        {methodSub ? (
          <div className="muted voucher-notes">{methodSub}</div>
        ) : null}
      </td>
      <td className="payments-cell-amount">
        € {eur}
        {gc && gc.refunded_cents > 0 && row.status !== 'refunded' ? (
          <div className="muted voucher-notes">
            – €{(gc.refunded_cents / 100).toFixed(2)} terugbetaald
          </div>
        ) : null}
        {row.discount ? (
          <div className="muted voucher-notes">
            – €{(row.discount.amount_off_cents / 100).toFixed(2)} korting ({row.discount.code})
          </div>
        ) : null}
      </td>
      <td>
        <span className={`voucher-pill ${statusClass}`}>{statusLabel}</span>
      </td>
      <td>
        {row.stripe_payment_intent_id ? (
          <a
            href={stripePaymentIntentUrl(row.stripe_payment_intent_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-link"
          >
            Stripe →
          </a>
        ) : null}
      </td>
    </tr>
  );
}
